// ============================================================================
// Central content data for MPeoples HRMS.
// This is the single source of truth pulled from the HRMS product
// documentation — pages, nav menus and cards all read from here.
// ============================================================================

// Only the Admin Dashboard screenshots are kept as real images — every
// other module/solution visual is generated at render time as an animated
// FlowChart (see src/components/FlowChart.jsx) instead of a static image.
import dashboardOverview from "../assets/screenshots/dashboard-overview.png";
import aboutDashboardWide from "../assets/about/admin-dashboard-wide.png";
import stores from "../assets/screenshots/ayya.jpg";
import myconnect from "../assets/screenshots/muthu.jpeg";
import heroapp from "../assets/screenshots/hero-app.jpg";

export const IMAGES = {
  heroapp,
  dashboardOverview,
  aboutDashboardWide,
};

// ---------------------------------------------------------------------------
// Tech stack (Product page)
// ---------------------------------------------------------------------------
export const TECH_STACK = [
  {
    tag: "Mobile",
    name: "Flutter & Dart",
    detail:
      "The employee-facing mobile app is built with Flutter, giving a single codebase and a consistent experience across Android devices.",
  },
  {
    tag: "Admin Web",
    name: "React JS",
    detail:
      "The Admin Dashboard is a separate React JS web application, built with reusable components and role-based views for HR and management.",
  },
  {
    tag: "Backend",
    name: "PHP Laravel",
    detail:
      "A Laravel backend handles business logic, database access, authentication and a RESTful API layer shared by the app and dashboard.",
  },
  {
    tag: "Messaging",
    name: "Firebase (FCM)",
    detail:
      "Firebase Cloud Messaging is integrated to deliver push notifications for HRMS updates and approvals.",
  },
];

// ---------------------------------------------------------------------------
// Application modules (Product > Modules)
// ---------------------------------------------------------------------------
export const MODULES = [
  {
    id: "login-auth",
    name: "Login & Authentication",
    category: "core",
    detail:
      "Secure access through employee login, credential authentication, session/token management and logout.",
  },
  {
    id: "dashboard",
    name: "Dashboard",
    category: "core",
    detail:
      "The central entry point after login, showing key information and quick access to major HRMS modules.",
  },
  {
    id: "attendance",
    name: "Attendance",
    category: "attendance",
    detail:
      "Manages daily attendance information, including viewing records, check-in/check-out access, and history.",
  },
  {
    id: "checkin-checkout",
    name: "Check-in / Check-out",
    category: "attendance",
    detail:
      "Digitally records the start and end of a working period, working with selfie and location verification, with ticket raising if it fails.",
  },
  {
    id: "selfie-attendance",
    name: "Selfie-Based Attendance",
    category: "attendance",
    detail:
      "Uses the device camera to capture a selfie during attendance operations, helping reduce proxy attendance.",
  },
  {
    id: "location",
    name: "Location / Latitude & Longitude",
    category: "attendance",
    detail:
      "Captures geographical location during attendance operations to support location-based verification.",
  },
  {
    id: "multi-branch",
    name: "Multi-Branch Management",
    category: "attendance",
    detail:
      "Supports organizations operating across multiple offices, associating employees with their workplace location.",
  },
  {
    id: "leave-management",
    name: "Leave Management",
    category: "leave",
    detail:
      "Employees submit leave requests, track them, view status, and maintain leave history.",
  },
  {
    id: "permission-management",
    name: "Permission Management",
    category: "leave",
    detail:
      "Handles short-duration permission requests; eligible unused time can be converted into leave per policy.",
  },
  {
    id: "holiday",
    name: "Holiday",
    category: "leave",
    detail:
      "Provides employees with organizational holiday information to help plan leave and work schedules.",
  },
  {
    id: "late-minutes",
    name: "Late Minutes",
    category: "attendance",
    detail:
      "Tracks late arrivals and late minutes, and applies the configured salary deduction rule.",
  },
  {
    id: "monthly-attendance",
    name: "Monthly Attendance",
    category: "attendance",
    detail:
      "A consolidated monthly view of attendance, including working days and late minutes.",
  },
  {
    id: "reports",
    name: "Reports",
    category: "self-service",
    detail:
      "Access to attendance-related reports, including monthly attendance information and downloadable records.",
  },
  {
    id: "profile",
    name: "Profile",
    category: "self-service",
    detail: "Access to employee details and relevant profile information.",
  },
  {
    id: "notifications",
    name: "Notifications",
    category: "self-service",
    detail:
      "Delivers important application and HR-related updates to employees.",
  },
  {
    id: "payroll",
    name: "Payroll",
    category: "payroll",
    detail:
      "Access to monthly payroll information, with the ability to view and download the payslip.",
  },
  {
    id: "admin-dashboard",
    name: "Admin Dashboard",
    category: "admin",
    featured: true,
    detail:
      "A web console for HR and management to manage employees, branches, attendance, leave, permissions, tickets, payroll and reports.",
    image: "dashboardOverview",
  },
];

export const MODULE_CATEGORIES = [
  { id: "core", label: "Core Access" },
  { id: "attendance", label: "Attendance & Verification" },
  { id: "leave", label: "Leave & Permission" },
  { id: "payroll", label: "Payroll" },
  { id: "self-service", label: "Employee Self-Service" },
  { id: "admin", label: "Admin Dashboard" },
];

// ---------------------------------------------------------------------------
// Solutions — problem / solution pairs
// ---------------------------------------------------------------------------
export const SOLUTIONS_OVERVIEW = [
  {
    problem: "Manual attendance processes",
    solution:
      "Digital check-in and check-out replace manual registers, with a ticket that can be raised whenever an action fails or is missed.",
  },
  {
    problem: "Proxy attendance",
    solution:
      "Selfie-based verification, combined with location data, reduces the possibility of proxy attendance and makes the process more transparent.",
  },
  {
    problem: "No visibility into where attendance happens",
    solution:
      "Latitude and longitude are captured during attendance operations, so location can be verified against the authorized workplace.",
  },
  {
    problem: "Manual coordination for unused permission time",
    solution:
      "Employees can convert eligible unused permission time into leave in line with organizational policy, without manual back-and-forth with HR.",
  },
  {
    problem: "Manual late-attendance salary calculations",
    solution:
      "Late minutes are evaluated automatically against a configured threshold, and the resulting salary deduction is calculated and itemized in payroll.",
  },
  {
    problem: "Attendance spread across branches",
    solution:
      "Multi-branch support associates employees with their respective locations, so attendance can be managed centrally across every workplace.",
  },
  {
    problem: "No central point of control for HR",
    solution:
      "A Web based Admin Dashboard lets HR and management configure and monitor employees, attendance, leave, permissions, tickets and payroll from one console.",
  },
];

// Solution detail pages, each grouping the relevant modules/workflow/benefits
export const SOLUTIONS = [
  {
    slug: "attendance",
    name: "Attendance & Verification",
    tagline: "Selfie and location-verified check-in and check-out",
    description:
      "Digital check-in and check-out replace manual registers. Selfie-based verification combined with GPS location data reduces proxy attendance and gives HR a transparent, verifiable record for every workplace.",
    moduleIds: [
      "attendance",
      "checkin-checkout",
      "selfie-attendance",
      "location",
      "late-minutes",
      "monthly-attendance",
    ],
    workflowSteps: [
      "Employee opens Attendance and selects check-in or check-out.",
      "The app captures a selfie and the current GPS location.",
      "The workplace location is validated before the entry is submitted.",
      "A ticket can be raised if the action fails or was missed.",
    ],
    benefits: [
      "Digitizes the employee attendance process.",
      "Provides selfie-based attendance verification.",
      "Captures latitude and longitude during attendance.",
      "Tracks late attendance and late minutes.",
    ],
  },
  {
    slug: "leave-permission",
    name: "Leave & Permission Management",
    tagline: "Centralized requests, approvals and conversion policy",
    description:
      "Employees submit leave requests and short-duration permission requests, track their status, and maintain a full history — with eligible unused permission time convertible into leave under organizational policy.",
    moduleIds: ["leave-management", "permission-management", "holiday"],
    workflowSteps: [
      "Employee selects a leave type and dates, or a permission time slot.",
      "A reason is entered and the request is submitted.",
      "The employee tracks approval status and history.",
      "Eligible unused permission time can be converted into leave.",
    ],
    benefits: [
      "Provides centralized leave and permission management, including conversion of unused permission time into leave.",
      "Provides employees with organizational holiday information to help plan leave and work schedules.",
    ],
  },
  {
    slug: "payroll",
    name: "Payroll & Late-Minute Deductions",
    tagline: "Automated deduction rules, payslip access on demand",
    description:
      "Late attendance is evaluated automatically against a configured threshold, with the resulting salary deduction calculated and itemized directly on the employee's payslip — removing a manual payroll calculation step.",
    moduleIds: ["payroll", "late-minutes"],
    workflowSteps: [
      "Late minutes are tracked automatically throughout the pay period.",
      "Accumulated late minutes are evaluated against the configured threshold.",
      "Any resulting deduction is calculated and itemized during payroll processing.",
      "Employees view and download their payslip from the app.",
    ],
    benefits: [
      "Automatically calculates salary deductions for late attendance based on configured late-minute rules.",
      "Provides employees with digital payslip access and download through the Payroll module.",
    ],
  },
  {
    slug: "multi-branch",
    name: "Multi-Branch Management",
    tagline: "One system across every workplace location",
    description:
      "MPeoples HRMS supports organizations operating across multiple offices, associating each employee with their respective workplace location so attendance can be verified and managed centrally.",
    moduleIds: ["multi-branch", "admin-dashboard"],
    workflowSteps: [
      "Each branch is configured with its authorized location.",
      "Employees are associated with their respective branch.",
      "Attendance location is validated against the assigned branch.",
      "HR monitors attendance across all branches from one Admin Dashboard.",
    ],
    benefits: [
      "Supports centralized management across multiple branches or locations.",
    ],
  },
  {
    slug: "admin-dashboard",
    name: "Admin Dashboard",
    tagline: "One console for HR and management",
    description:
      "A web-based Admin Dashboard, complements the mobile application — letting HR and management centrally manage employees, branches, attendance, leave, permissions, tickets, payroll and reports.",
    moduleIds: ["admin-dashboard", "reports"],
    // image: "dashboardOverview",
    workflowSteps: [
      "HR or an administrator logs in to the Web based Admin Dashboard.",
      "Records are reviewed or searched — attendance, leave, permissions, tickets or payroll.",
      "Requests are approved, rejected or updated as needed.",
      "Changes reflect back to the employee's mobile app.",
    ],
    benefits: [
      "Provides a dedicated, web-based Admin Dashboard for HR and management to configure and monitor the application.",
    ],
  },
];

// ---------------------------------------------------------------------------
// Features
// ---------------------------------------------------------------------------
export const FEATURES = [
  {
    name: "Selfie-based verification",
    detail:
      "Employees submit a selfie while recording attendance, reducing the possibility of proxy attendance.",
    iconKey: "selfie",
  },
  {
    name: "GPS / location-based attendance",
    detail:
      "Latitude and longitude are captured during attendance operations to verify the location of the transaction.",
    iconKey: "gps",
  },
  {
    name: "Check-in / check-out ticket raising",
    detail:
      "If check-in or check-out fails or is missed, the employee can raise a ticket requesting HR to review and correct the entry.",
    iconKey: "ticket",
  },
  {
    name: "Permission-to-leave conversion",
    detail:
      "Employees can convert eligible unused permission time into leave in accordance with organizational policy.",
    iconKey: "convert",
  },
  {
    name: "Payroll & payslip access",
    detail:
      "Employees view and download their monthly payslip directly from the app, reflecting attendance and deductions.",
    iconKey: "payroll",
  },
  {
    name: "Multi-branch / multi-location support",
    detail:
      "MPeoples HRMS supports organizations across multiple branches, associating employees with their respective locations.",
    iconKey: "branch",
  },
  {
    name: "Monthly attendance & late-minute tracking",
    detail:
      "Employees review monthly attendance and monitor late minutes for visibility into attendance patterns.",
    iconKey: "clock",
  },
  {
    name: "Web-based Admin Dashboard",
    detail:
      "A Web based Admin Dashboard lets HR and management manage employees, attendance, leave, permissions, tickets, branches, payroll and reports centrally.",
    iconKey: "dashboard",
  },
];

// ---------------------------------------------------------------------------
// How it works — employee & admin workflow, plus API flow
// ---------------------------------------------------------------------------
export const WORKFLOW_STEPS = [
  {
    title: "Login & Dashboard",
    detail:
      "The employee logs in and, upon successful authentication, is redirected to the Dashboard — with access to Attendance, Leave, Permission, Holidays, Payroll, Reports, Profile and Notifications.",
  },
  {
    title: "Attendance flow",
    detail:
      "The employee opens Attendance, selects check-in or check-out, captures a selfie and current location, and the app validates the workplace location before submitting. A ticket can be raised if the action fails or was missed.",
  },
  {
    title: "Leave, permission & holiday",
    detail:
      "Employees select a leave type and dates or a permission time slot, enter a reason, submit, and track status. Eligible unused permission time can be converted into leave.",
  },
  {
    title: "Monthly attendance, payroll & reports",
    detail:
      "Employees select a month for a consolidated attendance summary. Payroll reflects any late-minute deductions in the payslip, which can be viewed and downloaded.",
  },
  {
    title: "Profile, notifications & logout",
    detail:
      "Profile shows employee information, notifications communicate HRMS updates and approvals, and logout clears the authenticated session.",
  },
  {
    title: "Admin Dashboard flow",
    detail:
      "An HR/administrator logs in to the Web based Admin Dashboard, reviews or searches records, and approves, rejects or updates them — changes reflect back to the employee's app.",
  },
];

export const API_FLOW = [
  "Employee / Admin action",
  "Flutter app or React Admin Dashboard",
  "API request",
  "Laravel HRMS backend",
  "Request processing",
  "JSON response",
  "Result displayed",
];

// ---------------------------------------------------------------------------
// Benefits & business impact
// ---------------------------------------------------------------------------
export const BENEFITS = [
  "Digitizes the employee attendance process.",
  "Provides selfie-based attendance verification.",
  "Captures latitude and longitude during attendance.",
  "Enables check-in and check-out through the mobile app, with the option to raise a ticket if it fails or is missed.",
  "Provides centralized leave and permission management, including conversion of unused permission time into leave.",
  "Automatically calculates salary deductions for late attendance based on configured late-minute rules.",
  "Provides employees with digital payslip access and download through the Payroll module.",
  "Tracks late attendance and late minutes.",
  "Supports monthly attendance reports and downloads.",
  "Supports centralized management across multiple branches or locations.",
  "Provides a dedicated, web-based Admin Dashboard for HR and management to configure and monitor the application.",
];

export const IMPACT_PILLARS = [
  {
    title: "Transparency",
    detail:
      "Combining selfie verification with location information gives a more transparent attendance process than a basic check-in / check-out system.",
  },
  {
    title: "Efficiency",
    detail:
      "Late-minute salary deductions are evaluated and calculated automatically during payroll processing, and itemized on the payslip — removing a manual calculation step.",
  },
  {
    title: "Reliability",
    detail:
      "Testing across functional, UI, API, device, production and multi-location scenarios helps ensure a consistent experience for employees and organizations.",
  },
  {
    title: "Security",
    detail:
      "Role-based access on the Admin Dashboard, authorized-location validation, and permissions requested only when needed keep employee and payroll data protected.",
  },
];

// ---------------------------------------------------------------------------
// Resources
// ---------------------------------------------------------------------------
export const RESOURCES = [
  {
    slug: "api-documentation",
    title: "API documentation",
    detail:
      "RESTful API reference covering authentication, attendance, tickets, leave, permissions, holidays, payroll and admin endpoints.",
  },
  {
    slug: "security",
    title: "Permissions & security",
    detail:
      "Device permissions, authentication and token handling, data protection, and role-based access control on the Admin Dashboard.",
  },
  {
    slug: "testing",
    title: "Testing overview",
    detail:
      "How functional, UI, API, device, production and multi-location testing are approached across the application.",
  },
  {
    slug: "deployment",
    title: "Deployment",
    detail:
      "Android app distribution through Google Play Console, and how the backend and Admin Dashboard are deployed and connected over HTTPS.",
  },
  {
    slug: "troubleshooting",
    title: "Troubleshooting",
    detail:
      "Guidance for common login, location, camera, API, network, ticket, payroll and Admin Dashboard issues.",
  },
];

// ---------------------------------------------------------------------------
// About — audiences & requirements
// ---------------------------------------------------------------------------
export const AUDIENCES = [
  {
    title: "Employees",
    detail:
      "Record attendance, apply for leave and permissions, raise attendance tickets, view payslips, and access HR information.",
  },
  {
    title: "HR teams",
    detail:
      "Monitor employee attendance, leave, permissions, tickets, and payroll records through the Admin Dashboard.",
  },
  {
    title: "Organization / management",
    detail:
      "Maintain centralized HR operations and monitor employee information.",
  },
  {
    title: "Multi-branch organizations",
    detail:
      "Support employee and attendance management across different workplace locations.",
  },
];

export const REQUIREMENTS = [
  "A compatible Android environment with a working camera, GPS, sufficient storage and correct date/time settings.",
  "An active internet connection over Wi-Fi, 4G, or 5G for authentication, attendance, and payroll retrieval.",
  "A modern desktop or laptop browser for HR and management using the Admin Dashboard.",
];

// ---------------------------------------------------------------------------
// Clients — logo carousel (placeholder names; swap in real client logos
// and wordmarks under src/assets/clients before shipping)
// ---------------------------------------------------------------------------
export const CLIENTS = [
  { name: "Ayya Stores", logo: stores },
  { name: "Muthu & Co", logo: myconnect },
  // { name: "Coral Bay Logistics" },
  // { name: "Vertex Manufacturing" },
  // { name: "Meridian Retail" },
  // { name: "Solaris Healthcare" },
  // { name: "Palmshore Hospitality" },
  // { name: "Ironclad Engineering" },
];

// ---------------------------------------------------------------------------
// FAQ (Home) — grounded strictly in the documented facts above
// ---------------------------------------------------------------------------
export const FAQS = [
  {
    q: "What platform is the employee mobile app built for?",
    a: "The employee-facing app is built for Android using Flutter, giving a single codebase and a consistent experience across devices.",
  },
  {
    q: "How does MPeoples HRMS prevent proxy attendance?",
    a: "Attendance is verified with a selfie captured at check-in/check-out combined with the device's GPS location, which is checked against the authorized workplace.",
  },
  {
    q: "What happens if check-in or check-out fails?",
    a: "The employee can raise a ticket from the app, and HR can review and correct the entry from the Admin Dashboard.",
  },
  {
    q: "How are late-minute salary deductions calculated?",
    a: "Late minutes are evaluated automatically against a configured threshold, and the resulting deduction is calculated and itemized on the payslip during payroll processing.",
  },
  {
    q: "Can unused permission time be used as leave?",
    a: "Yes — eligible unused permission time can be converted into leave in line with organizational policy.",
  },
  {
    q: "Does MPeoples HRMS support multiple office locations?",
    a: "Yes — multi-branch support associates employees with their respective locations so attendance can be managed centrally across every workplace.",
  },
  {
    q: "How do HR and management manage the system?",
    a: "Through a web-based Admin Dashboard, covering employees, branches, attendance, leave, permissions, tickets, payroll and reports.",
  },
];
